/**
 * Validation schema for buyer forms using Yup
 * Unified validation for both create and edit operations
 */

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import type { BuyerFormData } from '../types';

const validateRequiredString = (value: string | undefined | null): boolean =>
  Boolean(value?.trim());

export const buyerFormValidationSchema = yup.object({
  name: yup
    .string()
    .test('nameRequired', 'Buyer name is required', validateRequiredString)
    .required('Buyer name is required'),
  displayName: yup.string().optional(),
  fullAddress: yup
    .string()
    .test('addressRequired', 'Address is required', validateRequiredString)
    .required('Address is required'),
  deliveryComment: yup.string().optional(),
  addressCity: yup.string().optional(),
  addressCountry: yup.string().when('addressSource', {
    is: 'custom',
    then: (schema) => schema.required('Country is required'),
    otherwise: (schema) => schema.optional(),
  }),
  isTest: yup.boolean().required().default(false),
  // On creation a custom address may only come from a Places selection, so its
  // coordinates must be present — a buyer without them has no delivery routing,
  // time zone or distance calculations (CRM-360). Edits are exempt: buyers
  // created before this rule legitimately have no coordinates, and blocking
  // their unrelated edits would be worse than leaving the gap.
  addressLatitude: yup.number().when(['$mode', 'addressSource'], {
    is: (mode: string, addressSource: string) =>
      mode === 'create' && addressSource === 'custom',
    then: (schema) =>
      schema.required(
        'Pick the address from the Google suggestions so its coordinates are stored'
      ),
    otherwise: (schema) => schema.optional(),
  }),
  addressLongitude: yup.number().optional(),
  addressUTCOffset: yup.number().optional(),
  addressSource: yup
    .string()
    .oneOf(['custom', 'google'] as const)
    .default('custom'),
  addressGooglePlaceId: yup.string().optional(),
  addressGoogleAddressId: yup.string().optional(),
});

export const buyerFormResolver = yupResolver<BuyerFormData>(
  buyerFormValidationSchema
);
